// Run detail (design/isomorphic run-detail): the conversation and the pending question
// first, the status timeline, cost, broker calls and the host's view beside it. The
// conversation slot is a prop so the design preview can feed a sample conversation.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect } from "react";
import { useParams } from "react-router";

import { runQuery, runQuestionsQuery } from "../../api/queries";
import { useDevice } from "../../device/context";
import { useT } from "../../i18n";
import { useCurrentOrg } from "../../layout/currentOrg";
import { ErrorState, Loading } from "../../ui/states";
import { environmentQuery, environmentWorkspacesQuery, workflowQuery } from "../api";
import { cliName } from "../format";
import type { ConversationSlotProps } from "./RunConversation";
import { RunFacts, RunHeader } from "./RunHeader";
import { HostVisibilityCard, RunBrokerCallsCard, RunCostCard, RunTimelineCard } from "./RunSide";
import { useRunStatus } from "./useRunStatus";

export function RunDetail({ conversation }: { conversation: (props: ConversationSlotProps) => ReactNode }) {
  const t = useT().workloads.run;
  const { runId = "" } = useParams();
  const run = useQuery(runQuery(runId));
  const questions = useQuery(runQuestionsQuery(runId));
  const workflow = useQuery({ ...workflowQuery(run.data?.workflow_id ?? ""), enabled: run.isSuccess });
  const envId = workflow.data?.environment_id;
  const environment = useQuery({ ...environmentQuery(envId ?? ""), enabled: envId !== undefined });
  const workspaces = useQuery({ ...environmentWorkspacesQuery(envId ?? ""), enabled: envId !== undefined });
  const { orgs } = useCurrentOrg();
  const device = useDevice();
  const live = useRunStatus(device.kind === "linked" ? device.relay : undefined, runId);
  // Every live transition (a question asked, the run finished) refreshes the run and its
  // questions, so a watched run shows what the agent asked without a reload.
  const queryClient = useQueryClient();
  const liveStatus = live?.status;
  useEffect(() => {
    if (liveStatus) void queryClient.invalidateQueries({ queryKey: ["runs", runId] });
  }, [liveStatus, runId, queryClient]);

  if (run.isPending) return <Loading />;
  if (run.isError) return <ErrorState title={t.couldNotLoad} message={run.error.message} />;
  const status = live?.status ?? run.data.status;
  const org = orgs.find((o) => o.id === run.data.org_id);
  const workspace = workspaces.data?.find((w) => w.id === workflow.data?.workspace_id);
  const place = [environment.data?.name, workspace?.name].filter(Boolean).join(" · ");
  const where = [org?.name, place].filter(Boolean).join(" / ") || "—";
  const agent = workflow.data ? cliName(workflow.data.cli_provider) : t.agentFallback;

  return (
    <>
      <RunHeader run={run.data} status={status} workflow={workflow.data} />
      <RunFacts run={run.data} live={live} where={where} />
      <div className="grid gap-5 xl:grid-cols-12">
        <div className="min-w-0 space-y-5 xl:col-span-8">
          {questions.isError ? <ErrorState title={t.couldNotLoadQuestions} message={questions.error.message} /> : null}
          {conversation({ runId, questions: questions.data ?? [], agent, where: place || undefined })}
          {run.data.result_text || run.data.status_reason ? <ResultCard text={run.data.result_text} reason={run.data.status_reason} /> : null}
        </div>
        <div className="min-w-0 space-y-5 xl:col-span-4">
          <RunTimelineCard run={run.data} status={status} />
          <RunCostCard run={run.data} model={workflow.data?.model} />
          <RunBrokerCallsCard run={run.data} />
          <HostVisibilityCard />
        </div>
      </div>
    </>
  );
}

function ResultCard({ text, reason }: { text: string | null | undefined; reason: string | null | undefined }) {
  const t = useT().workloads.run;
  return (
    <section className="rounded-xl border border-muted bg-gray-0 p-5 dark:bg-gray-50 lg:p-6" aria-label={t.result}>
      <h3 className="mb-1 text-base font-semibold text-gray-900">{t.result}</h3>
      {reason ? <p className="mb-2 mt-0 text-[13px] text-gray-500">{reason}</p> : null}
      {text ? <pre className="m-0 whitespace-pre-wrap break-words font-mono text-[12.5px] leading-relaxed text-gray-800">{text}</pre> : null}
    </section>
  );
}
